import {
  AccessoryProductType,
  AirHandlerProductType,
  ConfigurationType,
  DuctingComponentProductType,
  SelectedSystemType
} from 'data/types';
import {
  DUCT_METRICS_UNAVAILABLE_NOTICE,
  DUCT_NOT_QUOTED_NOTICE_CUSTOMER,
  DUCT_SOURCING_NOTICE,
  annotateSuppliesWithStock,
  resolveZoneDuctingPlan
} from 'utils/duct-component-fallback';
import { plenumNeedForFit } from 'utils/concealed-plenum-dimensions';
import {
  ZoneDuctSuppliesType,
  buildZoneDuctSupplies
} from 'utils/duct-supplies';
import {
  deriveNeededZoneDuctingProducts,
  deriveReturnRequirement,
  deriveSupplyRequirement,
  findZoneDuctingEvaluation,
  getDuctingQuoteStatus,
  getZoneDuctingModes,
  isConcealedDuctProduct,
  resolveZoneDuctModeKey,
  quotedReturnCoverageLabel,
  quotedSupplyCoverageLabel
} from 'utils/ducting-products';
import {
  ductSuppliesModeForKey,
  ZoneDuctModesMapType
} from 'utils/zone-duct-modes';

export type ZoneDuctingQuoteSummaryType = {
  zoneUniqueId: string;
  zoneName: string;
  notices: string[];
  supplies: ZoneDuctSuppliesType | null;
};

type BuildDuctingQuoteSummaryParams = {
  system: SelectedSystemType;
  configurations: ConfigurationType[];
  ductingKitProducts: AccessoryProductType[];
  airHandlerProducts: AirHandlerProductType[];
  ductingComponents?: DuctingComponentProductType[];
  zoneDuctModes?: ZoneDuctModesMapType;
  quotedAccessoriesByZone?: Record<
    string,
    Array<{ productId: number; quantity: number }>
  >;
};

function resolveZoneQuotedAccessories(
  zoneUniqueId: string,
  system: SelectedSystemType,
  concealedZoneCount: number,
  quotedAccessoriesByZone?: Record<
    string,
    Array<{ productId: number; quantity: number }>
  >
): Array<{ productId: number; quantity: number }> {
  const hasZoneAttribution =
    quotedAccessoriesByZone && Object.keys(quotedAccessoriesByZone).length > 0;
  if (hasZoneAttribution) {
    return quotedAccessoriesByZone[zoneUniqueId] ?? [];
  }
  if (concealedZoneCount <= 1) {
    return system.quotableAccessories;
  }
  return [];
}

export function buildDuctingQuoteSummary({
  system,
  configurations,
  ductingKitProducts,
  airHandlerProducts,
  ductingComponents,
  zoneDuctModes,
  quotedAccessoriesByZone
}: BuildDuctingQuoteSummaryParams): ZoneDuctingQuoteSummaryType[] {
  const summaries: ZoneDuctingQuoteSummaryType[] = [];
  const sourceConfig = configurations.find(
    (config) => config.uniqueId === system.configuration.uniqueId
  );
  const concealedZoneCount = system.configuration.condensers.reduce(
    (count, condenser) =>
      count +
      condenser.zones.filter(
        (zone) =>
          zone.selectedProduct && isConcealedDuctProduct(zone.selectedProduct)
      ).length,
    0
  );

  system.configuration.condensers.forEach((condenser) => {
    const validSystems =
      sourceConfig?.condensers.find(
        (sourceCondenser) => sourceCondenser.uniqueId === condenser.uniqueId
      )?.validSystems ?? [];

    condenser.zones.forEach((zone) => {
      const zoneProduct = zone.selectedProduct;
      if (!zoneProduct || !isConcealedDuctProduct(zoneProduct)) return;

      const catalogProduct =
        airHandlerProducts.find(
          (product) => product.productId === zoneProduct.productId
        ) ?? zoneProduct;

      const ducting = findZoneDuctingEvaluation(
        validSystems,
        zone.uniqueId,
        zoneProduct.productId
      );
      const modes = getZoneDuctingModes(ducting);
      const preferredKey = resolveZoneDuctModeKey(
        modes,
        zoneDuctModes?.[zone.uniqueId]
      );
      const preferredEvaluation = preferredKey ? modes[preferredKey] : null;
      const suppliesMode = preferredKey
        ? ductSuppliesModeForKey(preferredKey)
        : 'flex_only';
      const selectedDucting = preferredEvaluation
        ? {
            airHandlerProductId: zoneProduct.productId,
            metrics: preferredEvaluation.metrics,
            reasons: preferredEvaluation.reasons
          }
        : ducting;
      const needed = deriveNeededZoneDuctingProducts({
        airHandlerProduct: catalogProduct,
        ducting: selectedDucting as typeof ducting,
        ductingKitProducts,
        mode: suppliesMode
      });

      const roomNamesByUniqueId = new Map(
        zone.zoneItems.map((item) => [item.structureItemId, item.name])
      );
      const zoneQuotedAccessories = resolveZoneQuotedAccessories(
        zone.uniqueId,
        system,
        concealedZoneCount,
        quotedAccessoriesByZone
      );
      const metrics = selectedDucting?.metrics ?? null;
      const supplies = metrics
        ? buildZoneDuctSupplies(
            metrics,
            roomNamesByUniqueId,
            {
              supplyCoveredByLabel: quotedSupplyCoverageLabel(
                deriveSupplyRequirement(metrics),
                zoneQuotedAccessories,
                ductingKitProducts
              ),
              returnCoveredByLabel: quotedReturnCoverageLabel(
                deriveReturnRequirement(metrics, suppliesMode),
                zoneQuotedAccessories,
                ductingKitProducts
              )
            },
            suppliesMode,
            plenumNeedForFit(needed?.plenumFit, needed?.requiredPlenumDims)
          )
        : null;

      if (!supplies) {
        summaries.push({
          zoneUniqueId: zone.uniqueId,
          zoneName: zone.name,
          notices: [DUCT_METRICS_UNAVAILABLE_NOTICE],
          supplies: null
        });
        return;
      }

      const plan = resolveZoneDuctingPlan(
        metrics,
        ductingComponents ?? [],
        suppliesMode,
        needed
      );
      const neededForQuote = needed
        ? {
            ...needed,
            supplyKit: plan.supply.source === 'kit' ? needed.supplyKit : null,
            returnKit: plan.return.source === 'kit' ? needed.returnKit : null
          }
        : null;
      const quoteStatus = neededForQuote
        ? getDuctingQuoteStatus(
            neededForQuote,
            zoneQuotedAccessories,
            ductingKitProducts
          )
        : null;

      const annotated = annotateSuppliesWithStock(
        supplies,
        ductingComponents ?? [],
        zoneQuotedAccessories
      );

      const notices: string[] = [];
      const missingFromQuote =
        (quoteStatus?.missingCount ?? 0) > 0 || annotated.notQuotedCount > 0;
      if (missingFromQuote) {
        notices.push(DUCT_NOT_QUOTED_NOTICE_CUSTOMER);
      } else if (annotated.missingCount > 0) {
        notices.push(DUCT_SOURCING_NOTICE);
      }

      summaries.push({
        zoneUniqueId: zone.uniqueId,
        zoneName: zone.name,
        notices,
        supplies: annotated.supplies
      });
    });
  });

  return summaries;
}
